import FileUploadIcon from "@mui/icons-material/FileUpload";
import React from "react";
import useDragging from "../hooks/useDragging";
import { HoverMsg, UploaderWrapper } from "../utils/style";
import { acceptedExt, checkType, getFileSizeMB } from "../utils/utils";
import DrawTypes from "./DrawTypes";

type Props = {
  readonly name?: string;
  readonly hoverTitle?: string;
  readonly types?: Array<string>;
  readonly classes?: string;
  readonly children?: JSX.Element;
  readonly maxSize?: number;
  readonly minSize?: number;
  readonly fileOrFiles?: Array<File> | File | null;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly multiple?: boolean;
  readonly onSizeError?: (arg0: string) => void;
  readonly onTypeError?: (arg0: string) => void;
  readonly onDrop?: (arg0: File | Array<File>) => void;
  readonly onSelect?: (arg0: File | Array<File>) => void;
  readonly handleChange?: (arg0: File | Array<File>) => void;
  readonly onDraggingStateChange?: (dragging: boolean) => void;
};
/**
 *
 * Draw a description on the frame
 * @param currFile - The uploaded file
 * @param uploaded - boolean to check if the file uploaded or not yet
 * @param typeError - boolean to check if the file has type errors
 * @param disabled - boolean to check if input is disabled
 * @param label - string to add custom label
 * @returns JSX Element
 *
 * @internal
 *
 */
const drawDescription = (
  currFile: Array<File> | File | null,
  uploaded: boolean,
  typeError: boolean,
  disabled: boolean | undefined,
  label: string | undefined
) => {
  if (typeError) {
    return <span>File type or size error, try with another file.</span>;
  }

  const renderLabel = () => {
    if (label) {
      return (
        <>
          <span>{label.split(" ")[0]}</span> {label.substring(label.indexOf(" ") + 1)}
        </>
      );
    }
    return (
      <>
        <span>Upload</span> or drop a file right here.
      </>
    );
  };

  return (
    <p>
      {(() => {
        if (disabled) {
          return <span>Upload disabled</span>;
        }
        if (!currFile && !uploaded) {
          return renderLabel();
        }
        return (
          <>
            <span>Uploaded successfully!</span> Upload another?
          </>
        );
      })()}
    </p>
  );
};

/**
 * File uploading main function
 * @param props - {name,
    hoverTitle,
    types,
    handleChange,
    classes,
    children,
    maxSize,
    minSize,
    fileOrFiles,
    onSizeError,
    onTypeError,
    onSelect,
    onDrop,
    onTypeError,
    disabled,
    label,
    multiple,
    onDraggingStateChange
  }
 * @returns JSX Element
 */
const FileUploader: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    name,
    hoverTitle,
    types,
    handleChange,
    classes,
    children,
    maxSize,
    minSize,
    fileOrFiles,
    onSizeError,
    onTypeError,
    onSelect,
    onDrop,
    disabled,
    label,
    multiple,
    onDraggingStateChange,
  } = props;
  const labelRef = React.useRef<HTMLLabelElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploaded, setUploaded] = React.useState(false);
  const [currFiles, setCurrFiles] = React.useState<Array<File> | File | null>(null);
  const [error, setError] = React.useState(false);

  const validateFile = (file: File) => {
    if (types && !checkType(file, types)) {
      // types included and type not in them
      setError(true);
      if (onTypeError) onTypeError("File type is not supported");
      return false;
    }
    if (maxSize && getFileSizeMB(file.size) > maxSize) {
      setError(true);
      if (onSizeError) onSizeError("File size is too big");
      return false;
    }
    if (minSize && getFileSizeMB(file.size) < minSize) {
      setError(true);
      if (onSizeError) onSizeError("File size is too small");
      return false;
    }
    return true;
  };

  const handleChanges = (files: File | Array<File>): boolean => {
    let checkError = false;
    if (files) {
      if (files instanceof File) {
        checkError = !validateFile(files);
      } else {
        for (const file of files) {
          checkError = !validateFile(file) || checkError;
        }
      }
      if (checkError) return false;
      if (handleChange) handleChange(files);
      setCurrFiles(files);

      setUploaded(true);
      setError(false);
      return true;
    }
    return false;
  };

  const blockEvent = (ev: any) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  const handleClick = (ev: any) => {
    ev.stopPropagation();
    inputRef.current?.click();
  };

  const handleInputChange = (ev: any) => {
    const allFiles = ev.target.files;
    const files = multiple ? allFiles : allFiles[0];
    const success = handleChanges(files);
    if (onSelect && success) onSelect(files);
  };
  const dragging = useDragging({
    labelRef,
    inputRef,
    multiple,
    handleChanges,
    onDrop,
  });

  React.useEffect(() => {
    onDraggingStateChange?.(dragging);
  }, [dragging, onDraggingStateChange]);

  React.useEffect(() => {
    if (fileOrFiles) {
      setUploaded(true);
      setCurrFiles(fileOrFiles);
    } else {
      if (inputRef.current) inputRef.current.value = "";
      setUploaded(false);
      setCurrFiles(null);
    }
  }, [fileOrFiles]);

  return (
    <UploaderWrapper
      overRide={children}
      className={`${classes || ""} ${disabled ? "is-disabled" : ""} notification has-text-centered`}
      ref={labelRef}
      htmlFor={name}
      onClick={blockEvent}
    >
      <input
        onClick={handleClick}
        onChange={handleInputChange}
        accept={acceptedExt(types)}
        ref={inputRef}
        type="file"
        name={name}
        disabled={disabled}
        multiple={multiple}
      />
      {dragging && (
        <HoverMsg>
          <span>{hoverTitle || "Drop Here"}</span>
        </HoverMsg>
      )}
      {!children && (
        <div className="column is-centered">
          <div className="block">
            <FileUploadIcon fontSize="large" />
          </div>
          <div className="block">
            {drawDescription(currFiles, uploaded, error, disabled, label)}
          </div>
          <div className="block">
            <DrawTypes types={types} maxSize={maxSize} />
          </div>
        </div>
      )}
      {children}
    </UploaderWrapper>
  );
};
export default FileUploader;
